import { useState, useMemo, useEffect } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { CssBaseline, ThemeProvider, Box, CircularProgress } from '@mui/material';
import { createTheme } from '@mui/material/styles';
import posthog from 'posthog-js';

// Contexts
import { ThemeContext } from './contexts/ThemeContext';
import { AuthContext } from './contexts/AuthContext';

// Pages
import HomePage from './pages/HomePage';
import ListingsPage from './pages/ListingsPage';
import PropertyDetailPage from './pages/PropertyDetailPage';
import LoginPage from './pages/auth/LoginPage';
import SignupPage from './pages/auth/SignupPage';
import UserDashboard from './pages/dashboard/UserDashboard';
import SavedPropertiesPage from './pages/dashboard/SavedPropertiesPage';
import ProfilePage from './pages/dashboard/ProfilePage';
import AdminDashboard from './pages/admin/AdminDashboard';
import ManageListingsPage from './pages/admin/ManageListingsPage';
import AddPropertyPage from './pages/admin/AddPropertyPage';
import AgentsPage from './pages/AgentsPage';
import AgentDetailPage from './pages/AgentDetailPage';
import AgencyPage from './pages/AgencyPage'; // Existing generic agency page
import AgenciesListPage from './pages/AgenciesListPage'; // NEW
import AgencyDetailPage from './pages/AgencyDetailPage'; // NEW
import DevelopmentProjectsPage from './pages/DevelopmentProjectsPage'; // New
import ProjectDetailPage from './pages/ProjectDetailPage'; // New
import ChatPage from './pages/ChatPage'; // New Chat Page
import NotFoundPage from './pages/NotFoundPage';

// Components
import Layout from './components/layout/Layout';
import ProtectedRoute from './components/auth/ProtectedRoute';

// Theme
import { lightTheme, darkTheme } from './theme';

const App = () => {
  const [mode, setMode] = useState<'light' | 'dark'>('light');
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [user, setUser] = useState<any>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const location = useLocation();

  const theme = useMemo(
    () => createTheme(mode === 'light' ? lightTheme : darkTheme),
    [mode],
  );

  const themeContextValue = useMemo(
    () => ({
      mode,
      toggleColorMode: () => {
        setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
      },
    }),
    [mode],
  );

  const authContextValue = useMemo(
    () => ({
      isAuthenticated,
      user,
      login: (userData: any) => {
        setUser(userData);
        setIsAuthenticated(true);
        localStorage.setItem('user', JSON.stringify(userData));
        posthog.identify(userData.id, {
          name: userData.name,
          email: userData.email,
          role: userData.role,
        });
      },
      logout: () => {
        setUser(null);
        setIsAuthenticated(false);
        localStorage.removeItem('user');
        posthog.reset();
      },
    }),
    [isAuthenticated, user],
  );

  // Track page views
  useEffect(() => {
    posthog.capture('$pageview', {
      path: location.pathname,
      url: window.location.href,
    });
  }, [location]);

  // Check if user is already logged in
  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        setUser(userData);
        setIsAuthenticated(true);
        posthog.identify(userData.id, {
          name: userData.name,
          email: userData.email,
          role: userData.role,
        });
      } catch (error) {
        console.error('Failed to parse user data:', error);
        localStorage.removeItem('user');
      }
    }
    setIsLoading(false);
  }, []);

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <ThemeContext.Provider value={themeContextValue}>
      <AuthContext.Provider value={authContextValue}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <Routes>
            <Route path="/" element={<Layout />}>
              <Route index element={<HomePage />} />
              <Route path="listings" element={<ListingsPage />} />
              <Route path="property/:id" element={<PropertyDetailPage />} />
              <Route path="agents" element={<AgentsPage />} />
              <Route path="agents/:agentId" element={<AgentDetailPage />} />
              <Route path="agencies" element={<AgenciesListPage />} />
              <Route path="agencies/:agencyId" element={<AgencyDetailPage />} />
              <Route path="agency" element={<AgencyPage />} /> {/* Existing route */}

              {/* Development Project Routes */}
              <Route path="development-projects" element={<DevelopmentProjectsPage />} />
              <Route path="development-projects/:projectId" element={<ProjectDetailPage />} />

              <Route path="chat" element={<ChatPage />} /> {/* New Chat Route */}

              <Route path="login" element={<LoginPage />} />
              <Route path="signup" element={<SignupPage />} />
              
              {/* Protected Routes */}
              <Route element={<ProtectedRoute />}>
                <Route path="dashboard" element={<UserDashboard />} />
                <Route path="saved-properties" element={<SavedPropertiesPage />} /> {/* Restored SavedPropertiesPage */}
                <Route path="profile" element={<ProfilePage />} />
              </Route>

              {/* Admin Routes */}
              <Route element={<ProtectedRoute adminOnly />}>
                <Route path="admin" element={<AdminDashboard />} />
                <Route path="admin/listings" element={<ManageListingsPage />} />
                <Route path="admin/add-property" element={<AddPropertyPage />} />
              </Route>

              <Route path="*" element={<NotFoundPage />} />
            </Route>
          </Routes>
        </ThemeProvider>
      </AuthContext.Provider>
    </ThemeContext.Provider>
  );
};

export default App;